import { useEffect, useState } from "react";
import { logError } from "@/lib/logError";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Heart, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const isEn = () => localStorage.getItem("lang") === "en";

const WRAP = "min-h-screen flex items-center justify-center px-6 text-slate-100";
const BG = { background: "#0B1F33" };

function Card({ children }) {
  return (
    <div className={WRAP} style={BG}>
      <div className="max-w-md w-full rounded-2xl border border-white/10 bg-white/[0.04] p-10 text-center" data-testid="payment-result-card">
        {children}
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [info, setInfo] = useState(null);
  const en = isEn();

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      tries += 1;
      try {
        const r = await fetch(`${API}/api/payments/status/${sessionId}`);
        const d = await r.json();
        if (d.payment_status === "paid") { setInfo(d); setState("paid"); return; }
        if (["expired", "failed"].includes(d.payment_status)) { setState("error"); return; }
      } catch (e) { logError("Vérification du paiement (nouvel essai)")(e); }
      if (tries >= 8) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  if (state === "checking") {
    return <Card><Loader2 className="h-10 w-10 animate-spin mx-auto text-sky-300" />
      <p className="mt-5 text-sky-100/80">{en ? "Confirming your donation…" : "Confirmation de votre don…"}</p></Card>;
  }
  if (state === "paid") {
    return <Card>
      <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-400" />
      <h1 className="font-head text-2xl font-bold mt-5 text-white" data-testid="payment-success-title">{en ? "Thank you!" : "Merci !"}</h1>
      <p className="mt-3 text-sky-100/85 leading-relaxed">
        {en ? "Your donation of " : "Votre don de "}
        <b className="text-amber-200">{info?.amount} {(info?.currency || "cad").toUpperCase()}</b>
        {en ? " to the Dr Ahlem AOUACHRI-MAKHLOUF Foundation has been received."
            : " à la Fondation Dr Ahlem AOUACHRI-MAKHLOUF a bien été reçu."}
      </p>
      <Link to="/fondation" data-testid="payment-back-foundation"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold px-5 py-3 no-underline transition-colors">
        <Heart className="h-4 w-4" /> {en ? "Back to the foundation" : "Retour à la fondation"}
      </Link>
    </Card>;
  }
  const timeout = state === "timeout";
  return <Card>
    <XCircle className="h-14 w-14 mx-auto text-amber-400" />
    <h1 className="font-head text-xl font-bold mt-5 text-white">{en ? "Still processing" : "Traitement en cours"}</h1>
    <p className="mt-3 text-sky-100/80">
      {timeout ? (en ? "Your payment is taking longer than expected. If you were charged, your donation will be confirmed shortly."
                     : "Le paiement prend plus de temps que prévu. Si vous avez été débité, votre don sera confirmé sous peu.")
               : (en ? "We could not confirm this payment." : "Nous n'avons pas pu confirmer ce paiement.")}
    </p>
    <Link to="/fondation" className="mt-6 inline-flex items-center gap-2 text-sky-200 hover:text-white no-underline">
      <ArrowLeft className="h-4 w-4" /> {en ? "Back to the foundation" : "Retour à la fondation"}
    </Link>
  </Card>;
}

export function PaymentCancel() {
  const en = isEn();
  return <Card>
    <XCircle className="h-14 w-14 mx-auto text-slate-400" />
    <h1 className="font-head text-xl font-bold mt-5 text-white" data-testid="payment-cancel-title">{en ? "Donation cancelled" : "Don annulé"}</h1>
    <p className="mt-3 text-sky-100/80">{en ? "No payment was made. You can try again anytime." : "Aucun paiement n'a été effectué. Vous pouvez réessayer à tout moment."}</p>
    <Link to="/fondation" data-testid="payment-cancel-back"
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-semibold px-5 py-3 no-underline transition-colors">
      <ArrowLeft className="h-4 w-4" /> {en ? "Back to the foundation" : "Retour à la fondation"}
    </Link>
  </Card>;
}
